import { agent } from '../utils/agent'

const API_URL = process.env.REACT_APP_API_URL

/**
 * App Analytics V2 — reads the `app_events` table via /analytics-v2/*.
 *
 * Kept completely separate from AnalyticService.js (V1, legacy `analytics`
 * table) so the two can never collide. The shared `agent` already injects
 * Authorization and x-game-info.
 */

/**
 * Every V2 endpoint takes the same filter shape, so the query string is built
 * in one place. Empty / "all" values are omitted rather than sent, which keeps
 * the API's cache keys tidy.
 */
const buildParams = (filters = {}, extra = {}) => {
  const params = new URLSearchParams()

  if (filters.from) params.set('from', filters.from)
  if (filters.to) params.set('to', filters.to)
  if (filters.platform && filters.platform !== 'all') params.set('platform', filters.platform)
  if (filters.appVersion && filters.appVersion !== 'all')
    params.set('app_version', filters.appVersion)

  Object.keys(extra).forEach((key) => {
    const value = extra[key]
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, value)
    }
  })

  return params.toString()
}

const getSummary = async (filters) => {
  const response = await agent.get(`${API_URL}analytics-v2/summary?${buildParams(filters)}`)
  return response.data
}

const getTimeseries = async (filters, metric = 'sessions', interval = 'day') => {
  const response = await agent.get(
    `${API_URL}analytics-v2/timeseries?${buildParams(filters, { metric, interval })}`,
  )
  return response.data
}

const getPlatforms = async (filters) => {
  const response = await agent.get(`${API_URL}analytics-v2/platforms?${buildParams(filters)}`)
  return response.data
}

const getAppVersions = async (filters) => {
  const response = await agent.get(`${API_URL}analytics-v2/app-versions?${buildParams(filters)}`)
  return response.data
}

const getTopScreens = async (filters, limit = 200, offset = 0) => {
  const response = await agent.get(
    `${API_URL}analytics-v2/top-screens?${buildParams(filters, { limit, offset })}`,
  )
  return response.data
}

const getScreenFlow = async (filters, pageName) => {
  const response = await agent.get(
    `${API_URL}analytics-v2/screen-flow?${buildParams(filters, { page_name: pageName })}`,
  )
  return response.data
}

const AnalyticsV2Service = {
  buildParams,
  getSummary,
  getTimeseries,
  getPlatforms,
  getAppVersions,
  getTopScreens,
  getScreenFlow,
}

export default AnalyticsV2Service
